import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from './ui/alert-dialog';
import { Briefcase, Search, MapPin, DollarSign, ChevronLeft, Check, Sparkles } from 'lucide-react';

const GULF_COUNTRIES = ['UAE', 'Saudi Arabia', 'Qatar', 'Kuwait', 'Bahrain', 'Oman'];

function companyInitial(name) {
    if (!name) return '?';
    return name.trim().charAt(0).toUpperCase();
}

function JobCard({ job, onOpen }) {
    return (
        <button
            type="button"
            onClick={() => onOpen(job)}
            className="w-full text-left border border-zinc-200 p-4 hover:border-zinc-900 transition-colors bg-white"
            data-testid={`public-job-${job.id}`}
        >
            <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-zinc-900 text-white flex items-center justify-center flex-shrink-0 font-heading text-lg font-bold" data-testid={`public-job-avatar-${job.id}`}>
                    {companyInitial(job.employer_company_name)}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-base truncate" data-testid={`public-job-title-${job.id}`}>{job.title}</h3>
                        {job.already_applied && (
                            <Badge variant="outline" className="rounded-none text-[10px] text-green-700 border-green-300 bg-green-50" data-testid={`public-job-applied-badge-${job.id}`}>
                                <Check className="w-3 h-3 mr-1" /> Applied
                            </Badge>
                        )}
                    </div>
                    <p className="text-xs text-zinc-500 mt-1 truncate">{job.employer_company_name || 'Company'}</p>
                    <div className="flex flex-wrap gap-3 mt-2 text-xs text-zinc-600">
                        <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" /> {job.currency || 'USD'} {job.salary}</span>
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {job.location}, {job.country}</span>
                    </div>
                    {job.benefits && (
                        <p className="text-[11px] text-zinc-500 mt-2 line-clamp-1">
                            <Sparkles className="w-3 h-3 inline mr-1" />{job.benefits}
                        </p>
                    )}
                </div>
            </div>
        </button>
    );
}

function JobDetail({ jobId, onBack, onApplied, canApply, blockReason }) {
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showConfirm, setShowConfirm] = useState(false);
    const [applying, setApplying] = useState(false);
    const [applyError, setApplyError] = useState('');
    const [applied, setApplied] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/jobs/public/${jobId}`);
            setJob(data.job);
            setApplied(!!data.job?.already_applied);
        } catch (err) {
            console.error(err);
            setApplyError(err.response?.data?.detail || 'Failed to load job.');
        } finally {
            setLoading(false);
        }
    }, [jobId]);

    useEffect(() => { load(); }, [load]);

    const submitApply = async () => {
        setApplying(true);
        setApplyError('');
        try {
            await api.post(`/jobs/apply/${jobId}`);
            setApplied(true);
            setShowConfirm(false);
            if (onApplied) onApplied();
        } catch (err) {
            setApplyError(err.response?.data?.detail || 'Failed to apply.');
        } finally {
            setApplying(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-2 border-zinc-900 border-t-transparent animate-spin" />
            </div>
        );
    }
    if (!job) {
        return (
            <div className="border border-zinc-200 p-8 text-center" data-testid="public-job-not-found">
                <p className="text-sm text-zinc-500 mb-4">This job is no longer available.</p>
                <Button onClick={onBack} className="rounded-none" data-testid="public-job-back-btn">Back</Button>
            </div>
        );
    }

    return (
        <div data-testid="public-job-detail">
            <Button variant="ghost" onClick={onBack} className="rounded-none h-9 px-3 text-xs uppercase tracking-[0.15em] font-bold mb-4" data-testid="public-job-back-btn">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back to Job Openings
            </Button>

            <div className="border border-zinc-200 p-5 mb-4">
                <div className="flex items-start gap-3 mb-4">
                    <div className="w-14 h-14 bg-zinc-900 text-white flex items-center justify-center flex-shrink-0 font-heading text-xl font-bold">
                        {companyInitial(job.employer_company_name)}
                    </div>
                    <div className="flex-1">
                        <h2 className="font-heading text-2xl font-bold" data-testid="public-job-detail-title">{job.title}</h2>
                        <p className="text-sm text-zinc-500 mt-1">{job.employer_company_name || 'Company'}</p>
                    </div>
                </div>
                <Separator className="mb-4" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                    <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-bold">Salary</p>
                        <p className="text-sm font-semibold mt-1">{job.currency || 'USD'} {job.salary}</p>
                    </div>
                    <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-bold">Location</p>
                        <p className="text-sm font-semibold mt-1">{job.location}, {job.country}</p>
                    </div>
                </div>
                {job.benefits && (
                    <div className="mb-4">
                        <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-bold">Benefits</p>
                        <p className="text-sm mt-1">{job.benefits}</p>
                    </div>
                )}
                {job.description && (
                    <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-bold">Description</p>
                        <p className="text-sm mt-1 whitespace-pre-wrap">{job.description}</p>
                    </div>
                )}
            </div>

            {applyError && !showConfirm && (
                <div className="border border-red-200 bg-red-50 p-3 mb-4 text-sm text-red-700" data-testid="public-job-apply-error">
                    {applyError}
                </div>
            )}

            {applied ? (
                <Button
                    className="w-full h-14 rounded-none bg-zinc-100 text-zinc-500 font-semibold cursor-not-allowed"
                    disabled
                    data-testid="public-job-applied-btn"
                >
                    <Check className="w-5 h-5 mr-2" /> Applied
                </Button>
            ) : !canApply ? (
                <div className="border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="public-job-block-reason">
                    {blockReason}
                </div>
            ) : (
                <Button
                    onClick={() => setShowConfirm(true)}
                    className="w-full h-14 rounded-none bg-zinc-900 text-white font-semibold"
                    data-testid="public-job-apply-btn"
                >
                    Apply now
                </Button>
            )}

            <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
                <AlertDialogContent className="rounded-none border-2 border-zinc-900">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Apply to this job?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Your profile and documents will be shared with the employer. If they're interested, they may review your profile, schedule an interview, or send you an offer directly.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    {applyError && (
                        <div className="border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="public-job-confirm-error">
                            {applyError}
                        </div>
                    )}
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-none" data-testid="public-job-cancel-apply">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            className="rounded-none bg-zinc-900 text-white"
                            onClick={submitApply}
                            disabled={applying}
                            data-testid="public-job-confirm-apply"
                        >
                            {applying ? 'Submitting...' : 'Confirm and apply'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

export default function WorkerJobBoard({ profile, documents, hasAcceptedOffer, onNavigateToMyApplications }) {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [countryFilter, setCountryFilter] = useState('all');
    const [salaryMin, setSalaryMin] = useState('');
    const [salaryMax, setSalaryMax] = useState('');
    const [openedJobId, setOpenedJobId] = useState(null);

    const hasPassport = (documents || []).some(d => d.doc_type === 'passport');

    const canApply = hasPassport && !hasAcceptedOffer;
    let blockReason = '';
    if (!hasPassport) {
        blockReason = 'Upload your passport before applying to jobs. You can do this from the Dashboard tab.';
    } else if (hasAcceptedOffer) {
        blockReason = 'You already have an accepted offer, so you cannot apply to new jobs.';
    }

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = {};
            if (searchQuery.trim()) params.search = searchQuery.trim();
            if (countryFilter && countryFilter !== 'all') params.country = countryFilter;
            if (salaryMin) params.salary_min = parseFloat(salaryMin);
            if (salaryMax) params.salary_max = parseFloat(salaryMax);
            const { data } = await api.get('/jobs/public', { params });
            setJobs(data.jobs || []);
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.detail || 'Failed to load job openings.');
        } finally {
            setLoading(false);
        }
    }, [searchQuery, countryFilter, salaryMin, salaryMax]);

    // Debounced load on filter change.
    useEffect(() => {
        const t = setTimeout(() => { load(); }, 250);
        return () => clearTimeout(t);
    }, [load]);

    if (openedJobId) {
        return (
            <JobDetail
                jobId={openedJobId}
                onBack={() => { setOpenedJobId(null); load(); }}
                onApplied={() => { load(); }}
                canApply={canApply}
                blockReason={blockReason}
            />
        );
    }

    return (
        <div data-testid="worker-job-board">
            <div className="mb-6">
                <h2 className="font-heading text-2xl font-bold mb-1">Job Openings</h2>
                <p className="text-sm text-zinc-500">Browse open jobs from Gulf employers and apply to the ones that fit.</p>
            </div>

            {!hasPassport && (
                <div className="border border-amber-200 bg-amber-50 p-3 mb-4 text-sm text-amber-800" data-testid="job-openings-passport-warning">
                    You'll need to upload your passport before applying. Head to the Dashboard tab to complete this.
                </div>
            )}
            {hasAcceptedOffer && (
                <div className="border border-blue-200 bg-blue-50 p-3 mb-4 text-sm text-blue-800" data-testid="job-openings-offer-lock">
                    You already have an accepted offer, so applications are paused. You can still browse — but new applications won't be accepted.
                </div>
            )}

            <div className="space-y-3 mb-4">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        placeholder="Search by job title or company..."
                        className="pl-10 h-12 rounded-none"
                        data-testid="public-jobs-search-input"
                    />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <Select value={countryFilter} onValueChange={setCountryFilter}>
                        <SelectTrigger className="h-11 rounded-none" data-testid="public-jobs-country-filter"><SelectValue placeholder="Country" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All countries</SelectItem>
                            {GULF_COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Input
                        type="number"
                        min="0"
                        value={salaryMin}
                        onChange={e => setSalaryMin(e.target.value)}
                        placeholder="Min salary"
                        className="h-11 rounded-none"
                        data-testid="public-jobs-salary-min"
                    />
                    <Input
                        type="number"
                        min="0"
                        value={salaryMax}
                        onChange={e => setSalaryMax(e.target.value)}
                        placeholder="Max salary"
                        className="h-11 rounded-none"
                        data-testid="public-jobs-salary-max"
                    />
                </div>
            </div>

            {error && (
                <div className="border border-red-200 bg-red-50 p-3 mb-4 text-sm text-red-700">{error}</div>
            )}

            {loading ? (
                <div className="flex items-center justify-center py-16">
                    <div className="w-8 h-8 border-2 border-zinc-900 border-t-transparent animate-spin" />
                </div>
            ) : jobs.length === 0 ? (
                <div className="text-center py-12 border border-zinc-200" data-testid="public-jobs-empty">
                    <Briefcase className="w-12 h-12 text-zinc-200 mx-auto mb-4" />
                    <p className="font-heading font-bold text-base mb-1">No open jobs match your filters</p>
                    <p className="text-sm text-zinc-500 max-w-md mx-auto">Try adjusting the search or clearing the country/salary filters.</p>
                </div>
            ) : (
                <div className="space-y-3" data-testid="public-jobs-list">
                    {jobs.map(j => <JobCard key={j.id} job={j} onOpen={(job) => setOpenedJobId(job.id)} />)}
                </div>
            )}

            {onNavigateToMyApplications && (
                <div className="mt-8 text-center">
                    <button
                        type="button"
                        onClick={onNavigateToMyApplications}
                        className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 hover:text-zinc-900"
                        data-testid="jump-to-my-applications"
                    >
                        View my applications →
                    </button>
                </div>
            )}
        </div>
    );
}
